import { Collapse, Text, theme } from '@/components';
import { convertFormulaConditionsToAmountSearchConditions } from '@/features/project/_utils/projects/convert-request-params';
import { useProjectListSearchQueryStates } from '@/features/search/_hooks/projects-search';
import {
  type ProjectSearchFormType,
  ProjectSearchSchema,
} from '@/features/search/_models/projects-search';
import { useForm } from '@/hooks';
import { convertAmountByUnit } from '@/utils';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SearchHistoryModal } from '../../../../project/projects/_components/SearchHistoryModal';
import {
  type ProjectsSearchSubmitType,
  getAmountSearchBaseBudgetTypeAndAmountData,
  getInitialValueOfOptionalFields,
} from '../../../_utils/projects-search';
import { useProjectsSearchFormProviderState } from '../_common/ProjectsSearchAccordionProvider';
import { ProjectsSearchFieldTags } from './ProjectsSearchFieldTags';
import { SearchForm } from './SearchForm';

const getYearValue = (year: number | null) => {
  if (!year) {
    return undefined;
  }
  return dayjs().year(year);
};

type Props = {
  initialFiscalYear?: string;
  onSubmit: (values: ProjectsSearchSubmitType) => Promise<void>;
  /** 主にテストで使用する。強制的に上書きされてしまうので取り扱いに注意 */
  forceInitialValues?: Partial<ProjectSearchFormType>;
};

export const ProjectsSearchAccordion: FC<PropsWithChildren<Props>> = ({
  children,
  initialFiscalYear,
  onSubmit,
  forceInitialValues,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const resetFormTrigger = useRef<boolean>(false);
  const [openSearchHistoryModal, setOpenSearchHistoryModal] = useState(false);
  const { cascaderTargetIds, accordionState, setAccordionState } =
    useProjectsSearchFormProviderState();

  // 支出先の検索ワードをリセットするため。booleanだと一瞬の変化で検知できないため数値で比較
  const [resetCount, setResetCount] = useState(0);
  const { searchQueryStates } = useProjectListSearchQueryStates();

  const defaultValues = useMemo<ProjectSearchFormType>(() => {
    const {
      startYear,
      endYear,
      lastImplementedFiscalYearRangeMin,
      lastImplementedFiscalYearRangeMax,
      amountSearchBaseTargetYear,
      amountSearchBaseBudgetType,
      amountSearchBaseAmountType,
      amountSearchRightTargetYear,
      amountSearchRightBudgetType,
      amountSearchRightAmountType,
      amountSearchType,
      amountSearchDiffAmount,
      amountSearchDiffAmountUnit,
      amountSearchDiffOperator,
      formula_conditions,
      ...rest
    } = searchQueryStates;

    const initialValueOfOptionalFields = getInitialValueOfOptionalFields({
      searchQueryStates,
      initialFiscalYearByGroup: initialFiscalYear,
    });

    // formula_conditionsがある場合は優先的に使用
    const amountSearchConditions = (() => {
      if (formula_conditions) {
        // 新形式のformula_conditionsから復元
        return convertFormulaConditionsToAmountSearchConditions(
          formula_conditions,
        );
      }

      if (amountSearchType || amountSearchBaseTargetYear) {
        // 旧形式のURLパラメータから新形式への変換（後方互換性のため）
        return [
          {
            amountSearchType: amountSearchType ?? null,
            amountSearchDiffAmountUnit: amountSearchDiffAmountUnit ?? 'amount',
            amountSearchDiffAmount:
              amountSearchDiffAmount === null
                ? null
                : amountSearchDiffAmountUnit === 'percent'
                  ? amountSearchDiffAmount
                  : convertAmountByUnit({
                      value: amountSearchDiffAmount,
                      unit: 'thousand',
                    }),
            amountSearchDiffOperator: amountSearchDiffOperator ?? null,
            amountSearchBaseYearWithBudgetType:
              getAmountSearchBaseBudgetTypeAndAmountData({
                amountTargetFiscalYearParam: amountSearchBaseTargetYear,
                budgetType: amountSearchBaseBudgetType,
                budgetAmount: amountSearchBaseAmountType,
              }),
            amountSearchBaseYearWithBudgetTypeEnd:
              getAmountSearchBaseBudgetTypeAndAmountData({
                amountTargetFiscalYearParam: amountSearchRightTargetYear,
                budgetType: amountSearchRightBudgetType,
                budgetAmount: amountSearchRightAmountType,
              }),
          },
        ];
      }

      // デフォルト値
      return [
        {
          amountSearchType: 'threshold' as const,
          amountSearchDiffAmountUnit: 'amount' as const,
          amountSearchDiffAmount: null,
          amountSearchDiffOperator: null,
          amountSearchBaseYearWithBudgetType: null,
          amountSearchBaseYearWithBudgetTypeEnd: null,
        },
      ];
    })();

    return {
      ...rest,
      nestedOrganizationIds: cascaderTargetIds.organizationIds ?? null,
      nestedOtherResponsibleOrganizationIds:
        cascaderTargetIds.otherResponsibleOrganizationIds ?? null,
      nestedExpenditureBudgetPurposeIds:
        cascaderTargetIds.expenditureBudgetPurposeIds ?? null,
      nestedPolicyIds: cascaderTargetIds.policyIds ?? null,
      period: [getYearValue(startYear), getYearValue(endYear)],
      lastImplementedFiscalYearPeriod: [
        getYearValue(lastImplementedFiscalYearRangeMin),
        getYearValue(lastImplementedFiscalYearRangeMax),
      ],
      amountSearchConditions,
      fiscalYear: dayjs().year(Number(initialValueOfOptionalFields.fiscalYear)),
      skipAdministrativeReformTeamEvaluation:
        initialValueOfOptionalFields.skipAdministrativeReformTeamEvaluation,
      ...(forceInitialValues ? forceInitialValues : {}),
    };
  }, [
    searchQueryStates,
    cascaderTargetIds,
    initialFiscalYear,
    forceInitialValues,
  ]);

  const formMethod = useForm<ProjectSearchFormType>({
    resolver: zodResolver(ProjectSearchSchema),
  });

  /**

   * 検索履歴からquery parameterを変更した際にformの値がresetされないため。
   * providerが保持しているcascaderTargetIdsなどを再更新する必要も出てくるため、SearchHistoryModal内ではresetしない
   * アコーディオンの変更によって発火してしまうためresetFormTriggerを採用
   */
  useEffect(() => {
    if (!resetFormTrigger.current) {
      formMethod.reset(defaultValues);
      resetFormTrigger.current = true;
    }
  }, [defaultValues, formMethod.reset]);

  const onResetPayment = useCallback(() => {
    setResetCount((v) => v + 1);
  }, []);
  return (
    <FormProvider {...formMethod}>
      <Collapse
        accordion
        activeKey={accordionState.basicOpen ? '1' : undefined}
        expandIcon={({ isActive }) =>
          isActive ? (
            <DownOutlined style={{ color: token.colorPrimary, fontSize: 12 }} />
          ) : (
            <RightOutlined
              style={{ color: token.colorPrimary, fontSize: 12 }}
            />
          )
        }
        onChange={(e) => {
          setAccordionState((v) => ({
            ...v,
            basicOpen: !!e.length,
          }));
        }}
        style={{
          background: token.colorBgBase,
          borderRadius: 0,
        }}
        items={[
          {
            id: 'filter-project-panel',
            key: '1',
            style: { borderRadius: 0 },
            // extra: type === 'project' && (
            //   <Flex
            //     gap={8}
            //     style={{ width: 80 }}
            //     onClick={(e) => {
            //       e.stopPropagation();
            //       setOpenSearchHistoryModal(true);
            //       logger.info('検索履歴が表示されました');
            //     }}
            //   >
            //     <HistoryOutlined />
            //     <Text color="primary" text={t('search-history')} />
            //   </Flex>
            // ),
            label: (
              <>
                {!accordionState.basicOpen ? (
                  <>
                    <Text color="primary" text={t('search-condition')} />
                    <ProjectsSearchFieldTags onResetPayment={onResetPayment} />
                  </>
                ) : (
                  <Text color="primary" text={t('search-condition')} />
                )}
              </>
            ),
            children: (
              <>
                <SearchForm
                  onSubmit={onSubmit}
                  onResetPayment={onResetPayment}
                  resetCount={resetCount}
                />
                {/* testで使用するため */}
                {children}
              </>
            ),
          },
        ]}
      />
      <SearchHistoryModal
        // page番号をresetするため
        key={openSearchHistoryModal.toString()}
        open={openSearchHistoryModal}
        onSearch={() => {
          resetFormTrigger.current = false;
        }}
        onCancel={() => {
          setOpenSearchHistoryModal(false);
        }}
      />
    </FormProvider>
  );
};
